import React from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from 'recharts';
import Larry from './Larry';

const METRIC_CONFIG = {
  pace_score:     { color: 'var(--accent)', label: 'Pace Consistency', suffix: '' },
  wpm:            { color: '#6B67FF', label: 'WPM', suffix: '' },
  filler_free:    { color: '#4AC57A', label: 'Filler-Free Score', suffix: '%' },
  vocab_score:    { color: '#8E8BFF', label: 'Vocabulary', suffix: '' },
  sentence_score: { color: '#FFBD4F', label: 'Sentence Variety', suffix: '' },
};

function avgNonNull(arr, key) {
  const vals = arr.map(d => d[key]).filter(v => v != null);
  return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
}

function computeRadarData(data_points) {
  if (!data_points?.length) return [];

  return [
    { metric: 'Pace', value: Math.round(avgNonNull(data_points, 'pace_score')) },
    { metric: 'WPM', value: Math.min(100, Math.round((avgNonNull(data_points, 'wpm') / 200) * 100)) },
    { metric: 'Filler-Free', value: Math.round(Math.max(0, 100 - avgNonNull(data_points, 'filler_rate'))) },
    { metric: 'Vocabulary', value: Math.round(avgNonNull(data_points, 'vocab_score')) },
    { metric: 'Sentence Variety', value: Math.round(avgNonNull(data_points, 'sentence_score')) },
  ];
}

export default function ProgressChart({ data, metricKey, mode = 'line' }) {
  if (mode === 'radar') {
    const radarData = computeRadarData(data);
    if (radarData.length === 0) {
      return (
        <div className="flex items-center justify-center h-64 font-bold" style={{ color: 'var(--text-faint)' }}>
          No data to display
        </div>
      );
    }

    return (
      <div className="relative flex items-center justify-center" style={{ height: 320 }}>
        {/* Larry floating near center */}
        <div className="absolute z-10 pointer-events-none" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -60%)' }}>
          <Larry mood="thinking" size={48} message="Here's your skill signature!" />
        </div>

        <ResponsiveContainer width="100%" height={320}>
          <RadarChart data={radarData} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
            <PolarGrid stroke="rgba(255,255,255,0.08)" />
            <PolarAngleAxis
              dataKey="metric"
              tick={{ fontSize: 11, fill: 'var(--text-secondary)', fontWeight: 600, fontFamily: 'Outfit' }}
            />
            <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
            <Radar
              dataKey="value"
              stroke="var(--accent)"
              strokeWidth={2}
              fill="var(--accent-dim)"
              dot={{ r: 4, fill: 'var(--accent)', strokeWidth: 0 }}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // Line chart mode
  const config = METRIC_CONFIG[metricKey] || { color: 'var(--accent)', label: metricKey, suffix: '' };

  const chartData = data.map(d => ({
    date: d.date,
    title: d.title,
    value: metricKey === 'filler_free'
      ? Math.round((100 - (d.filler_rate || 0)) * 10) / 10
      : (d[metricKey] ?? null),
  })).filter(d => d.value != null);

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-white/25 font-bold">
        No data to display
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.3)', fontWeight: 700 }}
          tickMargin={8}
        />
        <YAxis
          tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.3)', fontWeight: 700 }}
          tickMargin={8}
          width={45}
        />
        <Tooltip
          contentStyle={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-accent)',
            borderRadius: 12,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'Outfit',
            color: 'var(--text-primary)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          }}
          formatter={(val) => [`${val}${config.suffix}`, config.label]}
          labelFormatter={(label, payload) => {
            const point = payload?.[0]?.payload;
            return point?.title ? `${point.title} (${label})` : label;
          }}
        />
        <Line
          type="monotone"
          dataKey="value"
          stroke={config.color}
          strokeWidth={3}
          dot={{ r: 5, fill: config.color, stroke: 'var(--bg-canvas)', strokeWidth: 2 }}
          activeDot={{ r: 7, strokeWidth: 2, stroke: config.color, fill: 'var(--bg-canvas)' }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

export { METRIC_CONFIG };
